/**
 * Tipi eventi SSE `progress` (mirror backend conversationStreamEvents.ts).
 */

export type ConversationProgressEvent =
  | { type: 'response_language'; language: 'it' | 'en' | 'de' | 'fr' | 'es' }
  | { type: 'phase'; step: 'started' | 'routing' | 'resolving_context' }
  | { type: 'tool_selected'; toolName: string; label: string }
  | {
      type: 'query_running';
      label: string;
      dateRange?: { from: string; to: string };
    }
  | {
      type: 'rows_ready';
      rowCount: number;
      dateRange?: { from: string; to: string };
      connectors?: string[];
    }
  | {
      type: 'table_preview';
      columns: string[];
      rows: Array<Record<string, string | number | null>>;
      totalRows: number;
    }
  | { type: 'narrative_start' };

export type StreamProgressStep = {
  id: string;
  label: string;
  status: 'active' | 'done';
  detail?: string;
};

type ProgressLang = 'it' | 'en' | 'es' | 'fr' | 'de' | 'pt';

function t(
  lang: ProgressLang,
  labels: { it: string; en: string; es: string; fr: string; de: string; pt: string },
): string {
  return labels[lang] ?? labels.it;
}

function getToolLabel(toolName: string, lang: ProgressLang): string {
  const map: Record<string, { it: string; en: string; es: string; fr: string; de: string; pt: string }> =
    {
      analytics_query: {
        it: 'Analisi metriche',
        en: 'Metrics analysis',
        es: 'Analisis de metricas',
        fr: 'Analyse des metriques',
        de: 'Metrik-Analyse',
        pt: 'Analise de metricas',
      },
      year_over_year: {
        it: 'Confronto anno su anno',
        en: 'Year-over-year comparison',
        es: 'Comparacion interanual',
        fr: 'Comparaison annuelle',
        de: 'Jahresvergleich',
        pt: 'Comparacao anual',
      },
      top_customers: {
        it: 'Top clienti',
        en: 'Top customers',
        es: 'Mejores clientes',
        fr: 'Top clients',
        de: 'Top-Kunden',
        pt: 'Principais clientes',
      },
      month_split: {
        it: 'Suddivisione mensile',
        en: 'Monthly breakdown',
        es: 'Desglose mensual',
        fr: 'Repartition mensuelle',
        de: 'Monatliche Aufschlüsselung',
        pt: 'Detalhamento mensal',
      },
      multi_query: {
        it: 'Analisi multipla',
        en: 'Multi-query analysis',
        es: 'Analisis multiple',
        fr: 'Analyse multi-requetes',
        de: 'Mehrfachanalyse',
        pt: 'Analise multipla',
      },
      google_ads_negative_keyword_miner: {
        it: 'Audit search term Google Ads',
        en: 'Google Ads search term audit',
        es: 'Auditoria de terminos Google Ads',
        fr: 'Audit des termes Google Ads',
        de: 'Google-Ads-Suchbegriff-Audit',
        pt: 'Auditoria de termos do Google Ads',
      },
      link_checker_404: {
        it: '404 Link Checker',
        en: '404 Link Checker',
        es: 'Verificador de enlaces 404',
        fr: 'Verificateur de liens 404',
        de: '404-Link-Checker',
        pt: 'Verificador de links 404',
      },
      facebook_creative_analysis: {
        it: 'Analisi creative Meta',
        en: 'Meta creative analysis',
        es: 'Analisis creativo de Meta',
        fr: 'Analyse des creatives Meta',
        de: 'Meta-Creative-Analyse',
        pt: 'Analise de criativos Meta',
      },
      kipify_advisor: {
        it: 'Kipify growth pulse',
        en: 'Kipify growth pulse',
        es: 'Pulso de crecimiento Kipify',
        fr: 'Pulse croissance Kipify',
        de: 'Kipify Growth Pulse',
        pt: 'Pulso de crescimento Kipify',
      },
      meta_google_ad_copy_winners: {
        it: 'Copy Meta + Google',
        en: 'Meta + Google ad copy',
        es: 'Copy Meta + Google',
        fr: 'Copy Meta + Google',
        de: 'Meta + Google Copy',
        pt: 'Copy Meta + Google',
      },
      contextual_follow_up: {
        it: 'Approfondimento contestuale',
        en: 'Contextual follow-up',
        es: 'Seguimiento contextual',
        fr: 'Suivi contextuel',
        de: 'Kontextuelles Follow-up',
        pt: 'Follow-up contextual',
      },
    };

  const hit = map[toolName];
  return hit ? t(lang, hit) : toolName.replace(/_/g, ' ');
}

export function progressEventToSteps(
  events: ConversationProgressEvent[],
  lang: ProgressLang,
): StreamProgressStep[] {
  const steps: StreamProgressStep[] = [];

  for (const ev of events) {
    switch (ev.type) {
      case 'response_language':
        break;
      case 'phase':
        if (ev.step === 'started') {
          steps.push({
            id: 'phase-started',
            label: t(lang, {
              it: 'Avvio analisi…',
              en: 'Starting analysis…',
              es: 'Iniciando analisis…',
              fr: 'Demarrage de l analyse…',
              de: 'Analyse startet…',
              pt: 'Iniciando analise…',
            }),
            status: 'done',
          });
        } else if (ev.step === 'routing') {
          steps.push({
            id: 'phase-routing',
            label: t(lang, {
              it: 'Scelgo lo strumento giusto…',
              en: 'Choosing the right tool…',
              es: 'Eligiendo la herramienta adecuada…',
              fr: 'Choix du bon outil…',
              de: 'Passendes Tool wird gewählt…',
              pt: 'Escolhendo a ferramenta certa…',
            }),
            status: 'done',
          });
        } else if (ev.step === 'resolving_context') {
          steps.push({
            id: 'phase-context',
            label: t(lang, {
              it: 'Leggo il contesto precedente…',
              en: 'Reading prior context…',
              es: 'Leyendo el contexto previo…',
              fr: 'Lecture du contexte precedent…',
              de: 'Vorheriger Kontext wird gelesen…',
              pt: 'Lendo o contexto anterior…',
            }),
            status: 'done',
          });
        }
        break;
      case 'tool_selected':
        steps.push({
          id: `tool-${ev.toolName}`,
          label: getToolLabel(ev.toolName, lang),
          status: 'done',
        });
        break;
      case 'query_running': {
        const dr =
          ev.dateRange?.from && ev.dateRange?.to
            ? `${ev.dateRange.from} → ${ev.dateRange.to}`
            : undefined;
        steps.push({
          id: 'query-running',
          label: t(lang, {
            it: 'Recupero i dati…',
            en: 'Fetching data…',
            es: 'Recuperando datos…',
            fr: 'Recuperation des donnees…',
            de: 'Daten werden geladen…',
            pt: 'Buscando dados…',
          }),
          status: 'done',
          detail: dr,
        });
        break;
      }
      case 'rows_ready':
        steps.push({
          id: 'rows-ready',
          label: t(lang, {
            it: `Dati pronti (${ev.rowCount} righe)`,
            en: `Data ready (${ev.rowCount} rows)`,
            es: `Datos listos (${ev.rowCount} filas)`,
            fr: `Donnees pretes (${ev.rowCount} lignes)`,
            de: `Daten bereit (${ev.rowCount} Zeilen)`,
            pt: `Dados prontos (${ev.rowCount} linhas)`,
          }),
          status: 'done',
          detail: ev.connectors?.join(', '),
        });
        break;
      case 'narrative_start':
        steps.push({
          id: 'narrative',
          label: t(lang, {
            it: 'Scrivo la risposta…',
            en: 'Writing the answer…',
            es: 'Escribiendo la respuesta…',
            fr: 'Redaction de la reponse…',
            de: 'Antwort wird geschrieben…',
            pt: 'Escrevendo a resposta…',
          }),
          status: 'active',
        });
        break;
      default:
        break;
    }
  }

  const deduped: StreamProgressStep[] = [];
  const seen = new Set<string>();
  for (const s of steps) {
    if (seen.has(s.id)) {
      const idx = deduped.findIndex((x) => x.id === s.id);
      if (idx >= 0) deduped[idx] = s;
      continue;
    }
    seen.add(s.id);
    deduped.push(s);
  }
  if (deduped.length > 0) {
    const last = deduped[deduped.length - 1]!;
    if (last.id !== 'narrative' && !events.some((e) => e.type === 'narrative_start')) {
      deduped.push({
        id: 'waiting-narrative',
        label: t(lang, {
          it: 'Preparo la sintesi…',
          en: 'Preparing summary…',
          es: 'Preparando el resumen…',
          fr: 'Preparation du resume…',
          de: 'Zusammenfassung wird vorbereitet…',
          pt: 'Preparando o resumo…',
        }),
        status: 'active',
      });
    }
  }
  return deduped;
}
